import { getUIComponent } from "@builder/store/slices/BuilderSlice/builderMap";
import store from "@builder/store/store";
import { CommonPageType, getPageSpecificComponents } from "@builder/utils/JSXtoBuilder/architecture/pagesAndComponentsInfo";
import { _upperCaseFirstLetter } from "@builder/utils/JSXtoBuilder/jsxHelpers";
import { JSX_NODE_MAPPINGS } from "@builder/utils/JSXtoBuilder/jsxMapping";
import type { BaseLayerTypes, BuildComponentKey } from "@common/build/types";
import { getBuildSize } from "../helpers";



const getRecommendedComponents = (pageType?: CommonPageType) => {
    const componentsToUse: string[] = [];
    if (pageType) {
        componentsToUse.push(...getPageSpecificComponents(pageType));
        componentsToUse.push(...Object.keys(JSX_NODE_MAPPINGS)
        .filter(key => !getUIComponent(key as BuildComponentKey).exclude && !JSX_NODE_MAPPINGS[key as BuildComponentKey]?.typicallyUsedOnPage));
    }
    else {
        componentsToUse.push(...Object.keys(JSX_NODE_MAPPINGS).map(_upperCaseFirstLetter));
    }
    return componentsToUse;
}


export const stylingTips = `
Prefer flex, keep layouts minimal and clean. Use hex colors or existing variables. 
Remove unnecessary nesting, remove unnecessary Divs. If you see a long chain of divs in divs, it's likely unnecessary, always try to clean it up. 
When designing a full page, make sure interior content fills the whole page.
Images should typically be large, and display cover. You typically don't need to keep them inside a container - Image component is already wrapped in a div.
`

export const globalColorsPrompt = `If you are planning any color changes you are considering, you will want to get their color scheme beforehand, use the getColorScheme tool. Not if you don't do any design though. Always prefer to use their colors vars, unless they don't have ones to fit the task.
You may also use similar colors which could pair well with the existing colors. If there is an important color they're lacking in their color scheme, add it using the addColorToColorScheme tool.
This tool will return the var you can use in your CSS.
`

export const dynamicDataPrompt = `
Core Concept: Use dynamic data extensively - almost every text/image should show real information (customer names, prices, order totals, etc.) rather than static placeholders.
Context System:

orderSection - for order/receipt pages (shows order's customer, items, totals). For example in an order, customer.billing.address1 and customer.shipping.address1 are related to the order.
productSection - for product displays (shows selected product details)
customerSection - for customer displays (shows selected customer info)
general - default context (limited data available)

In dynamic grids, the repeaterDiv functions like a context. So in product grid/list, the repeaterDiv is product context, in customer list, the repeaterDiv is customer and so on.

Section information will show the info about the selected item in a dynamic grid. FOR EXAMPLE:
Product grid might have a repeaterDiv whith product.name. This will render a list of product names based on the query of the product grid. RepeaterDiv will repeat itself with different product every time.
Once one of the repeaterDivs is clicked, it becomes the selected product in the state, and all the product sections dynamic data will refer to that selected product.
Same goes for customer list and section, order list and section, etc.

Key Rules:

Always search first - Use searchDynamicData tool to find available keys before adding any dynamic data
Use exact keys - Only use dynamic data keys that exist in search results, spelled exactly as shown
Match context - Add appropriate section wrapper if needed for the data you want to display
You may with text - Use prepend/append props to add labels (e.g., prepend="Price: " for prices). No need to add currency symbols though, they are added automatically.

Implementation:

Modify dynamicData prop with the correct key (e.g., "customer.name", "product.price")
Choose dynamic data with the proper type: string / number / currency / time could go in a text node, image could have image dynamic data, etc.
Context determines what data is available (same key shows different data in different contexts) and also what the data refers to (e.g. customer.name in customer list refers to the customer name, in product list it's not available, and in order list it's the order customer name)

Remember: Dynamic data makes the POS functional - use it generously, users need to see real information!
`

const advancedComponentsPrompt = `
IMPORTANT:
ANY creation or editing of dynamic grids (ProductList, ProductGrid, CustomerList, OrderList), ALWAYS use the tool createOrEditDynamicGrid. 
It will give you the JSX you need. Then you can edit the external styles of the grid to make it fit the page, but don't make any other changes yourself.
Since the dynamic grid is scrollable, you will typically keep it in some big container, and (other than header or footer) you will not put anything under it or above it. You want to give it room to fill some main container.
`
const advancedPropsPrompt = `
some props may have extra instructions. Here:
- prepend / append: both have {value, style} to appear before / after dynamic data. They styles could be used to make them different colors or sizes than the dynamic data text.
`


export const generateDesignInstructionsPrompt= ({pageType, pageName, layerType}: {pageType?: CommonPageType, pageName: string, layerType: BaseLayerTypes}) => {

    const recommendedComponents = getRecommendedComponents(pageType);

    const generalPrompt = `
    You're a confident design assistant designed to help the user build their POS system using our JSX component library.
    You may use components which are already in the page, or you can add new ones / remove existing ones.
    These are the general components you are allowed to add if don't already exist in the page: ${recommendedComponents.join(", ")}
    
    some components have extra instructions, which you would see in a comment above them.
    These are the ONLY components you are allowed to add. Never make up components outside of this list or what you see in the page.

    The POS you are building is called ${store.getState().builder.build.name}. It's built for the size: ${getBuildSize()}.
    POS typically consists of multiple pages (like home, product, order, customer, checkout, etc.), it also has popups and customer facing display pages.
    You are currently designing a ${layerType} layer.
    Functionally, the page you are designing is likely to be a ${pageType ? pageType : "multi purpose"} page. It's named ${pageName}.
    
    Use only inline styles (no Tailwind, no JS, no libraries). 
    Preserve all id on modified elements. You can remove or add components freely, but when you modify an existing component, you must preserve its id.


    Once you know what JSX you need to put in the page, use the injectJSXIntoPage tool to inject it into the page.

    You MUST SEND IT VALID JSX.
    NO MARKDOWN, NO CODE FENCES, NO ESCAPED CHARACTERS, NO THINGS THAT ARE NOT STRING JSX. NO NEWLINES.
    for example: <Div>Hi</Div> or <BaseLayerPage>[more jsx]</BaseLayerPage>
    IF FOR SOME REASON YOU ARE UNABLE TO PERFORM THE TASK, explain to the user what you are unable to do.

    IF you don't use the injectJSXIntoPage tool, nothing is going to change.
    If it fails you likely fucked up the JSX format. Try again.

    `;

    return [generalPrompt, globalColorsPrompt, dynamicDataPrompt,advancedComponentsPrompt, advancedPropsPrompt, stylingTips].join("\n");
}

